import { useState, useEffect, useCallback, useRef, type CSSProperties, type ReactNode } from 'react';
import { motion, useAnimationControls, useReducedMotion, type Variants } from 'framer-motion';
import { weekShortLabel } from '@/lib/week-label';
import Image from 'next/image';
import { Check, X, ChevronDown, Info, Lock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { transitions } from '@/lib/motion';
import { useHaptics } from '@/hooks/useHaptics';
import { useToast } from '@/stores/ui.store';
import { Button } from '@/components/ui/button';
import { CountryFlag } from '@/components/ui/CountryFlag';
import { NflHelmet } from '@/components/ui/NflHelmet';
import { CountryPopover } from '@/components/ui/CountryPopover';
import { countryDisplayName } from '@/lib/country-flags';
import { getNflTeam } from '@/config/nfl-teams';
import { stripeColor } from '@/lib/team-stripe';
import type { GameData, PropData, MyPropData } from '@/services/events/main-event';
import type { PickSelection } from '@prisma/client';
import { asset } from '@/lib/asset';
import {
  CATEGORY_LABELS,
  buildPlayerQuestion,
  buildSpreadQuestion,
  buildTotalQuestion,
  canPush,
  extractPlayerName,
  findMainLine,
  findMainSpread,
  fmtPts,
  getAbbrev,
  getBaseCategory,
  getPlayerPropSide,
  getPointsUnit,
  getTotalLabel,
  getTotalNoun,
  getTotalPropSide,
  isLinelessProp,
} from './utils';
import { InlineManualPropRow } from './widgets';


// ============================================================================
// UNIFIED GAME CARD COMPONENTS
// ============================================================================

/** Small info tooltip that toggles on click */
export function InfoTooltip({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center justify-center w-[14px] h-[14px] rounded-full border border-warm-gray/40 text-warm-gray/60 hover:text-warm-gray transition-colors"
        aria-label="Info"
      >
        <Info className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute z-20 bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-56 px-3 py-2 rounded-lg bg-surface-3 border border-surface-4 text-[11px] text-cream/80 shadow-lg leading-snug">
          {text}
        </div>
      )}
    </div>
  );
}


/**
 * Team-color cue on pick buttons (design option C, user-picked 2026-07-28):
 * a slim primary + secondary double-stripe down one edge — echoes a
 * helmet's center stripe. Renders nothing for non-NFL teams, so every other
 * sport keeps the plain button. Burned/disabled buttons dim the stripe via
 * the button's own opacity. Away buttons stripe the left edge, home buttons
 * the right, so the pair frames the matchup like a scoreboard.
 */
function TeamStripe({ team, side = 'left' }: { team: string; side?: 'left' | 'right' }) {
  const t = getNflTeam(team);
  if (!t) return null;
  const left = side === 'left';
  return (
    // motion.span so the parent PickButton's 'pop' variant flashes the stripe
    <motion.span
      aria-hidden
      variants={stripeFlashVariants}
      className={cn('absolute inset-y-0 w-[9px] overflow-hidden', left ? 'left-0 rounded-l-lg' : 'right-0 rounded-r-lg')}
    >
      <span className={cn('absolute inset-y-0 w-[4px]', left ? 'left-0' : 'right-0')} style={{ background: stripeColor(t.primary) }} />
      <span className={cn('absolute inset-y-0 w-[3px]', left ? 'left-[5px]' : 'right-[5px]')} style={{ background: stripeColor(t.secondary) }} />
    </motion.span>
  );
}

/**
 * Pick-button keyframes. Selecting pops harder than deselecting settles —
 * the asymmetry is deliberate (gaining a pick should feel bigger than losing
 * one). TeamStripe inherits 'pop' via variant propagation and flashes.
 */
const pickButtonVariants: Variants = {
  pop: { scale: [1, 1.06, 1], transition: { duration: 0.16, ease: [0.2, 0.9, 0.2, 1] } },
  depop: { scale: [1, 0.97, 1], transition: { duration: 0.16, ease: [0.2, 0.9, 0.2, 1] } },
  shake: { x: [0, -4, 4, -3, 3, 0], transition: { duration: 0.18 } },
};

const stripeFlashVariants: Variants = {
  pop: { opacity: [1, 0.4, 1], transition: { duration: 0.16 } },
};


// ============================================================================
// SETTLEMENT — the weekly payoff moment
// ============================================================================

/** Per-card offset when a parent staggers a whole slate settling at once. */
const SETTLE_STAGGER_MS = 60;
/** Win: the color flood is quick and lands with the pop. */
const SETTLE_WIN_FLOOD_MS = 340;
/** Loss: the same flood, slowed down until it reads as a sigh, not a hit. */
const SETTLE_LOSS_FLOOD_MS = 620;

/**
 * One-shot "this game just settled" signal.
 *
 * Returns 0 on mount — including a mount where the game is ALREADY settled —
 * and increments only when `settled` flips false → true on a live update. That
 * is the whole reason opening a page of finished games doesn't pop sixteen
 * cards at once.
 *
 * The comparison runs during render (React's documented adjust-state-while-
 * rendering pattern) rather than in an effect, because the settled color
 * classes and the widened settle transition timing have to land in the SAME
 * commit. From an effect the 120ms micro transition would already be running
 * by the time we could slow it down.
 */
function useSettleSignal(settled: boolean): number {
  const [seen, setSeen] = useState(settled);
  const [signal, setSignal] = useState(0);
  if (seen !== settled) {
    setSeen(settled);
    if (settled) setSignal((n) => n + 1);
  }
  return signal;
}

/**
 * Cross-card gate for the settle haptic.
 *
 * A whole slate can settle inside a single poll while the page is open, so
 * sixteen sibling cards fire their settle effect in the same tick — and the
 * stagger deliberately spreads them past the 40ms coalesce window in
 * lib/haptics, so that guard would not catch them either. Sixteen buzzes is a
 * malfunction, not a celebration.
 *
 * The first card to settle in a batch therefore claims the buzz and every
 * other card inside the window stays silent. Module scope on purpose: these
 * cards are siblings with no shared state to hang this off, and the vibration
 * motor is a single global resource regardless of how the tree is shaped.
 *
 * Tradeoff, accepted: if the first card in a batch is a loss and a later one is
 * a win, the batch buzzes 'impactLight'. Buffering the batch to prefer the win
 * would mean holding the haptic until every card reported in, which is a worse
 * failure mode than an occasionally understated buzz.
 */
const SETTLE_HAPTIC_WINDOW_MS = 1500;
let lastSettleHapticAt = -Infinity;
function claimSettleHaptic(now: number = Date.now()): boolean {
  if (now - lastSettleHapticAt < SETTLE_HAPTIC_WINDOW_MS) return false;
  lastSettleHapticAt = now;
  return true;
}

/**
 * The win flood: a verdigris wash that blooms up the button once and clears.
 *
 * Mounted only for a live correct settle, so its entrance animation IS the
 * trigger — no prev-value ref needed here. Losses get no wash at all; the
 * asymmetry is the point.
 *
 * DOM order does NOT put this underneath the team name: CSS paints positioned
 * z-index:auto descendants (step 6) ABOVE in-flow inline content (step 5), so
 * being the first child bought nothing and the 0.7-opacity wash covered the
 * winning team's name and score on the payoff frame. The button's content
 * carries `relative z-10` instead, which is what actually keeps it on top.
 *
 * rounded-lg, not -md: this is an inset-0 overlay inside a rounded-lg button
 * with no overflow-hidden, so a 6px radius let verdigris nubs poke out past
 * each of the button's 8px corners.
 */
function SettleFlood({ delay }: { delay: number }) {
  return (
    <motion.span
      aria-hidden
      className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-t from-verdigris via-verdigris/50 to-transparent"
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 0.7, 0] }}
      transition={{ duration: 0.55, delay, times: [0, 0.3, 1], ease: 'easeOut' }}
    />
  );
}

/**
 * Settled result glyph in the center column. A correct pick springs in; a wrong
 * one only fades. Mounts already-settled on page open (signal 0) and stays
 * inert, so the spring is reserved for the moment it actually happens.
 */
function SettleGlyph({ correct, signal, delay }: { correct: boolean; signal: number; delay: number }) {
  const controls = useAnimationControls();
  const reducedMotion = useReducedMotion();
  const played = useRef(0);

  useEffect(() => {
    if (signal === 0 || signal === played.current) return;
    played.current = signal;
    if (reducedMotion) return;
    if (correct) {
      controls.set({ scale: 0.3, opacity: 0 });
      controls.start({ scale: 1, opacity: 1, transition: { type: 'spring', stiffness: 400, damping: 15, delay } });
    } else {
      controls.set({ opacity: 0 });
      controls.start({ opacity: 1, transition: { ...transitions.context, delay } });
    }
  }, [signal, correct, delay, reducedMotion, controls]);

  return (
    <motion.span className="inline-block" initial={false} animate={controls}>
      {correct
        ? <Check className="w-4 h-4 text-verdigris-text mx-auto" />
        : <X className="w-4 h-4 text-oxblood-text mx-auto" />}
    </motion.span>
  );
}

/**
 * Rendered helmet size inside a pick button, in px.
 *
 * 28px, chosen against the real geometry rather than the design note's 44px:
 * that number was written when pick buttons were 227px wide, and Phase 2's card
 * widening left them 124×64 side-by-side at a 390px viewport (~101px of content
 * once the 2px border and px-2.5 are removed). 28px is small enough to stack
 * above a full-width team name inside a 72px button — 28 + 2 gap + two 17.5px
 * clamped lines = 65px of the 68px available — and large enough to read as the
 * same asset as the 42px header helmet rather than as an icon.
 */
const PICK_HELMET_PX = 28;

/**
 * Team identity inside a pick button: helmet (NFL) or flag (everyone else),
 * the name, and the survivor "used" footnote.
 *
 * NFL pick buttons used to be name-only on the theory that the helmet lived in
 * the card header — but the header is glanced at and the pick button is what
 * gets tapped, so the signature asset now rides on the control that carries the
 * decision. It sits BESIDE the name when the buttons are full width and ABOVE
 * it once they go side-by-side, where a 124px button cannot afford ~34px of
 * horizontal room without clipping the longest city names ("Jacksonville",
 * "Philadelphia", "Indianapolis" all run ~83px at text-sm). Stacking hands the
 * name back the full width; the extra 8px of button height pays for the helmet.
 *
 * The helmet is centered, so it never reaches the 9px team stripe on the
 * button's outer edge.
 */
function TeamPickFace({ team, seed, score, showScore, usedWeek, picked, facing }: {
  team: string;
  seed: number | null;
  score: number | null;
  /** Game settled and a score exists — append it to the name. */
  showScore: boolean;
  /** SURVIVOR: week this team was burned, or undefined when it's still live. */
  usedWeek: number | undefined;
  picked: boolean;
  /** Helmets point inward at each other: away faces right, home faces left. */
  facing: 'left' | 'right';
}) {
  const nfl = getNflTeam(team);
  const burned = usedWeek !== undefined;
  const label = (
    <>
      {seed && <span className="text-warm-gray/60 mr-1">({seed})</span>}
      {countryDisplayName(team)}
    </>
  );

  return (
    <>
      {/* relative z-10, not decoration: SettleFlood is an absolutely positioned
          sibling, and CSS paints those above in-flow inline content no matter
          the DOM order. Without this the win wash covers the name it is
          celebrating. See the note on SettleFlood. */}
      <span
        className={cn(
          'relative z-10 flex min-w-0 items-center justify-center gap-1.5',
          nfl && '@xs:flex-col @xs:gap-0.5',
        )}
      >
        {nfl ? (
          <NflHelmet
            team={nfl}
            size={PICK_HELMET_PX}
            facing={facing}
            className={cn(
              'transition-opacity duration-[var(--duration-micro)]',
              picked
                ? 'opacity-100 drop-shadow-[0_1px_3px_rgba(245,240,232,0.35)]'
                : 'opacity-50',
            )}
          />
        ) : (
          <CountryFlag name={team} size="md" className="shrink-0" />
        )}
        {/* Name block. In the stacked NFL layout it reserves a fixed two-line
            box (2 x 17.5px at text-sm/leading-tight = 35px) and baseline-aligns
            to its top, so a one-line name and a two-line name leave the helmet
            at the same y. Without it, "Los Angeles (C)" wrapping to two lines
            lifted its helmet 8.8px above a one-line opponent's and the pair
            read as misaligned. Height budget for the h-[72px] border-2 button:
            28 helmet + 2 gap + 35 name = 65 of the 68px inner box. */}
        <span className={cn('flex min-w-0 items-baseline justify-center gap-1', nfl && '@xs:h-[35px]')}>
          {/* break-words is a backstop, not a layout choice: every NFL city fits
              one line at full button width (longest is ~83px of ~101px), but a
              name that ever did overflow should wrap to the second clamped line
              rather than get sliced off mid-word by line-clamp's overflow. */}
          <span className={cn('min-w-0 text-sm leading-tight', burned ? 'truncate' : 'line-clamp-2 break-words')}>
            {label}
            {showScore && !burned && <span className="text-xs opacity-70"> ({score})</span>}
          </span>
          {/* Burned buttons hold the name to one line — helmet + two lines
              would collide with the footnote below — so the score has to live
              OUTSIDE the clamp or the clamp eats it: a settled survivor week
              rendered "Jacksonville (1…", truncating the score itself. As a
              shrink-0 sibling the score always survives; the city ellipsises
              instead. (The score is NOT readable off the opposite button — that
              one carries the other team's score.) */}
          {showScore && burned && (
            <span className="shrink-0 text-xs leading-tight opacity-70">({score})</span>
          )}
        </span>
      </span>
      {/* Absolutely pinned to the BUTTON's bottom edge (the button is the
          nearest positioned ancestor), deliberately outside the centred column
          above. In flow it added ~13px to the burned side only, pushing that
          name ~6px off its neighbour's baseline in any survivor row where
          exactly one team is burned. Out of flow it lands in the empty second
          line of the reserved name box: content occupies y 1.5-66.5 of the 68px
          inner box, the one-line burned name ends at y 49, and bottom-1 puts
          the footnote at y 55-64 — clear of both. */}
      {burned && (
        <span className="absolute inset-x-0 bottom-1 z-10 text-[9px] leading-none text-warm-gray/50">
          used {weekShortLabel(usedWeek)}
        </span>
      )}
    </>
  );
}

/**
 * Tactile game-winner pick button: press-scale, selection pop, and haptics.
 * Burned (survivor-used) buttons stay tappable so a rejected tap can shake +
 * buzz 'error' — aria-disabled + an onClick guard replace the disabled
 * attribute for that state only; locked/settled buttons keep real disabled.
 */
function PickButton({ selected, burned, burnedTeam, burnedWeek, locked, replacesOther, settleResult, settleSignal = 0, settleDelay = 0, onSelect, style, className, children }: {
  selected: boolean;
  /** SURVIVOR: team already used — non-functional, but taps get feedback. */
  burned: boolean;
  /** Team on this button, for the burned-tap message. */
  burnedTeam?: string;
  /** Week the team was burned, for the burned-tap message. */
  burnedWeek?: number;
  /** Picks closed (event locked / game settled) — fully inert. */
  locked: boolean;
  /** True when tapping this button swaps the pick off the other side. */
  replacesOther: boolean;
  /**
   * How this button resolved, on the settled side only — null for the button
   * the user didn't pick, which stays out of the payoff moment entirely.
   */
  settleResult?: 'correct' | 'incorrect' | null;
  /** One-shot trigger from useSettleSignal; 0 means "settled before we mounted". */
  settleSignal?: number;
  /** Seconds to hold before the settle animation, for staggering a slate. */
  settleDelay?: number;
  onSelect: () => void;
  style?: CSSProperties;
  className?: string;
  children: ReactNode;
}) {
  const { fire } = useHaptics();
  const { warning } = useToast();
  const reducedMotion = useReducedMotion();
  const controls = useAnimationControls();
  const prevSelected = useRef(selected);
  const playedSettle = useRef(0);
  const actionable = !locked && !burned;

  // Pop only on selected-flag *transitions* — the ref is seeded with the
  // current value so the initial mount (or remount with a pick) never pops.
  useEffect(() => {
    if (prevSelected.current === selected) return;
    prevSelected.current = selected;
    if (reducedMotion) return;
    controls.start(selected ? 'pop' : 'depop');
  }, [selected, reducedMotion, controls]);

  // Settle pop — wins only. A loss settles on the color alone (slower, no
  // scale): losing should not get the same physical acknowledgement as winning.
  // Same prev-value guard as the selection pop, keyed on the signal rather than
  // the flag so an already-settled mount stays still.
  useEffect(() => {
    if (settleSignal === 0 || settleSignal === playedSettle.current) return;
    playedSettle.current = settleSignal;
    if (reducedMotion || settleResult !== 'correct') return;
    controls.start({
      scale: [1, 1.04, 1],
      transition: { duration: 0.42, delay: settleDelay, ease: [0.2, 0.9, 0.2, 1] },
    });
  }, [settleSignal, settleResult, settleDelay, reducedMotion, controls]);

  // One haptic per tap, at click time. There is deliberately NO press-time
  // (onTapStart) buzz: it fired ~70-100ms before this one — outside the 40ms
  // coalesce window, so a single tap buzzed twice — and because these cards sit
  // in a horizontal snap-x carousel with no onTapCancel partner, it also fired
  // on every swipe between matchups. impactLight vs impactMedium already
  // distinguishes a fresh pick from one that replaces the other side.
  const handleTap = () => {
    if (locked) return;
    if (burned) {
      fire('error');
      if (!reducedMotion) controls.start('shake');
      // The shake and the buzz are BOTH reduced-motion gated (useHaptics gates,
      // and lib/haptics gates again on the same media query), so this tap was a
      // silent no-op for a reduced-motion user — and for a screen-reader user,
      // since aria-disabled leaves the button focusable and activatable. The
      // toast is the non-motion channel that always lands; the store dedupes on
      // title, so repeat taps on the same team don't stack. Haptic explicitly
      // null — the 'error' buzz above already owns the tactile half.
      warning(
        burnedTeam ? `${burnedTeam} already used` : 'Team already used',
        burnedWeek !== undefined
          ? `Burned in ${weekShortLabel(burnedWeek)} — each team is one and done.`
          : 'Each team is one and done.',
        null,
      );
      return;
    }
    if (!selected) fire(replacesOther ? 'impactMedium' : 'impactLight');
    onSelect();
  };

  return (
    <motion.button
      onClick={handleTap}
      disabled={locked}
      aria-disabled={burned || undefined}
      aria-pressed={selected}
      style={style}
      className={className}
      variants={pickButtonVariants}
      initial={false}
      animate={controls}
      whileTap={actionable ? { scale: 0.96 } : undefined}
      transition={transitions.micro}
    >
      {children}
    </motion.button>
  );
}

/** Game Winner row — replaces GamePickRow inside the unified card */
export function GameWinnerRow({ game, pick, onPick, disabled, pointsPerCorrectPick, usedTeams, showPoints = true, settleIndex = 0 }: {
  game: GameData;
  pick: PickSelection | null;
  onPick: (gameId: string, winner: PickSelection) => void;
  disabled: boolean;
  pointsPerCorrectPick: number;
  /** SURVIVOR: team display name → week it was burned. That side renders disabled. */
  usedTeams?: Map<string, number>;
  /** Hide the "(N points if correct)" suffix — points are meaningless in survivor. */
  showPoints?: boolean;
  /**
   * Position of this game in the list, used only to stagger the settle
   * animation when a whole slate resolves at once (delay = index × 60ms).
   * Defaults to 0, so a caller that doesn't care gets no delay.
   */
  settleIndex?: number;
}) {
  const { fire } = useHaptics();
  const reducedMotion = useReducedMotion();
  const settled = game.winner !== null;
  const settleSignal = useSettleSignal(settled);
  const settleDelayMs = settleIndex * SETTLE_STAGGER_MS;
  const settleDelaySec = settleDelayMs / 1000;
  const pickCorrect = settled && pick !== null && game.winner === pick;
  const playedSettleHaptic = useRef(0);

  // Settle haptic — one buzz, once, and only if the user actually had a pick
  // riding on this game. claimSettleHaptic() is what keeps a sixteen-game slate
  // resolving in one poll from turning into sixteen buzzes.
  useEffect(() => {
    if (settleSignal === 0 || settleSignal === playedSettleHaptic.current) return;
    playedSettleHaptic.current = settleSignal;
    if (pick === null) return;
    if (!claimSettleHaptic()) return;
    fire(pickCorrect ? 'success' : 'impactLight');
  }, [settleSignal, pick, pickCorrect, fire]);

  // Survivor: a burned team can never be re-picked — gray its button out
  // (unless it IS the current pick, so the selection never looks burned).
  const awayUsedWeek = pick !== 'AWAY' ? usedTeams?.get(game.awayTeam) : undefined;
  const homeUsedWeek = pick !== 'HOME' ? usedTeams?.get(game.homeTeam) : undefined;

  // Compute the per-button class for a given pick value. Shared logic so
  // the visual states stay in sync across both team buttons.
  function buttonClass(buttonPick: PickSelection): string {
    const isPicked = pick === buttonPick;
    if (!isPicked) {
      return 'bg-surface-3 border-2 border-transparent text-warm-gray hover:border-surface-4';
    }
    if (!settled) {
      return 'bg-gold/20 border-2 border-gold text-gold';
    }
    const correct = game.winner === pick;
    return correct
      ? 'bg-verdigris/20 border-2 border-verdigris text-verdigris-text'
      : 'bg-oxblood/20 border-2 border-oxblood text-oxblood-text';
  }

  // The verdigris/oxblood flood itself is the existing CSS color transition,
  // widened at the moment of settling: quick for a win, near twice as slow for
  // a loss. Nothing is applied when the game was already settled on mount
  // (signal 0) — and a CSS transition can't fire on first paint anyway, which
  // is the second reason a page of finished games opens quietly.
  const settleFloodStyle: CSSProperties | undefined = settleSignal > 0
    ? {
        transitionDuration: `${pickCorrect ? SETTLE_WIN_FLOOD_MS : SETTLE_LOSS_FLOOD_MS}ms`,
        transitionDelay: `${settleDelayMs}ms`,
      }
    : undefined;

  const settleResult = (side: PickSelection): 'correct' | 'incorrect' | null => {
    if (!settled || pick !== side) return null;
    return pickCorrect ? 'correct' : 'incorrect';
  };
  /** The win wash — live correct settles only, and never under reduced motion. */
  const floodSide = settleSignal > 0 && pickCorrect && !reducedMotion ? pick : null;

  // NFL matchups stack the helmet above the name once the buttons sit
  // side-by-side, which needs 8px more height than the two-clamped-lines box.
  // Every other sport keeps the original 64px button exactly.
  const nflMatchup = getNflTeam(game.awayTeam) !== null || getNflTeam(game.homeTeam) !== null;
  const buttonSize = nflMatchup ? 'h-16 @xs:h-[72px]' : 'h-16';

  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Game Winner</span>
        {showPoints && (
          <span className="text-[10px] text-warm-gray/40">({pointsPerCorrectPick} points if correct)</span>
        )}
      </div>
      <div className="flex flex-col @xs:flex-row @xs:items-stretch gap-2">
        {/* Away team — fixed height (fits 2 clamped lines, plus the helmet in
            the stacked NFL layout) so both team buttons stay uniform no matter
            how long the names are. */}
        <PickButton
          selected={pick === 'AWAY'}
          burned={awayUsedWeek !== undefined}
          burnedTeam={game.awayTeam}
          burnedWeek={awayUsedWeek}
          locked={disabled || settled}
          replacesOther={pick !== null && pick !== 'AWAY'}
          settleResult={settleResult('AWAY')}
          settleSignal={settleSignal}
          settleDelay={settleDelaySec}
          style={pick === 'AWAY' ? settleFloodStyle : undefined}
          onSelect={() => onPick(game.id, 'AWAY')}
          className={cn(
            'relative @xs:flex-1 px-2.5 rounded-lg text-xs font-bold transition-colors duration-[var(--duration-micro)] flex items-center justify-center text-center',
            buttonSize,
            awayUsedWeek !== undefined
              ? 'bg-surface-3/40 border-2 border-transparent text-warm-gray/50 opacity-60 cursor-not-allowed'
              : buttonClass('AWAY'),
            (disabled || settled) && 'cursor-default'
          )}
        >
          {floodSide === 'AWAY' && <SettleFlood delay={settleDelaySec} />}
          <TeamStripe team={game.awayTeam} />
          <TeamPickFace
            team={game.awayTeam}
            seed={game.awaySeed}
            score={game.awayScore}
            showScore={settled && game.awayScore !== null}
            usedWeek={awayUsedWeek}
            picked={pick === 'AWAY'}
            facing="right"
          />
        </PickButton>

        {/* VS / result indicator */}
        <div className="w-8 text-center flex-shrink-0 self-center @max-xs:hidden">
          {settled ? (
            pick ? (
              <SettleGlyph correct={pickCorrect} signal={settleSignal} delay={settleDelaySec} />
            ) : (
              <span className="text-xs text-tertiary">—</span>
            )
          ) : (
            <span className="text-[10px] text-tertiary font-bold">or</span>
          )}
        </div>

        {/* Home team — stripe mirrors to the right edge so the pair frames the
            matchup like a scoreboard rather than reading as two left rails. */}
        <PickButton
          selected={pick === 'HOME'}
          burned={homeUsedWeek !== undefined}
          burnedTeam={game.homeTeam}
          burnedWeek={homeUsedWeek}
          locked={disabled || settled}
          replacesOther={pick !== null && pick !== 'HOME'}
          settleResult={settleResult('HOME')}
          settleSignal={settleSignal}
          settleDelay={settleDelaySec}
          style={pick === 'HOME' ? settleFloodStyle : undefined}
          onSelect={() => onPick(game.id, 'HOME')}
          className={cn(
            'relative @xs:flex-1 px-2.5 rounded-lg text-xs font-bold transition-colors duration-[var(--duration-micro)] flex items-center justify-center text-center',
            buttonSize,
            homeUsedWeek !== undefined
              ? 'bg-surface-3/40 border-2 border-transparent text-warm-gray/50 opacity-60 cursor-not-allowed'
              : buttonClass('HOME'),
            (disabled || settled) && 'cursor-default'
          )}
        >
          {floodSide === 'HOME' && <SettleFlood delay={settleDelaySec} />}
          <TeamStripe team={game.homeTeam} side="right" />
          <TeamPickFace
            team={game.homeTeam}
            seed={game.homeSeed}
            score={game.homeScore}
            showScore={settled && game.homeScore !== null}
            usedWeek={homeUsedWeek}
            picked={pick === 'HOME'}
            facing="left"
          />
        </PickButton>
      </div>
    </div>
  );
}


/**
 * Point spread — one plain question, answered Yes or No.
 *
 * The market only ever asks one thing ("does the favourite win by enough?"), so
 * the card asks exactly that. The team toggle and the alt-line slider that used
 * to sit here are both gone: taking the underdog at +3.5 IS answering No to the
 * favourite covering -3.5, so the toggle was a second control for a choice the
 * answer already carries, and the slider made the user shop a decimal before
 * they could answer anything.
 */
export function SpreadSection({ game, props: spreadProps, myProps, purchasedPropIds, onPurchase, disabled, purchasingPropId }: {
  game: GameData;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const main = findMainSpread(spreadProps, game.homeTeam, game.awayTeam);

  const purchasedProp = spreadProps.find((p) => purchasedPropIds.has(p.id)) ?? null;
  const purchasedData = purchasedProp
    ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null
    : null;

  if (!main) return null;

  const margin = Math.abs(main.line);
  const question = buildSpreadQuestion(main.threshold, main.favourite);
  const tooltip = canPush(margin)
    ? `Yes if ${main.favourite} win by ${main.threshold} or more. Winning by exactly ${margin} refunds either answer.`
    : `Yes if ${main.favourite} win by ${main.threshold} or more, No if they fall short.`;

  const header = (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Spread</span>
      {!disabled && <InfoTooltip text={tooltip} />}
    </div>
  );

  // Answered. The side bought names the answer: the favourite's line is the
  // negative one, so a negative line means they said Yes.
  if (purchasedProp) {
    return (
      <div className="space-y-2">
        {header}
        <p className="text-[11px] text-cream/70 leading-snug">{question}</p>
        <div className="flex items-center gap-2 text-xs">
          <span className="font-bold text-cream">{(purchasedProp.line ?? 0) < 0 ? 'Yes' : 'No'}</span>
          <span className="text-warm-gray/50">{purchasedProp.bonusMultiplier}x</span>
          <PropResultChip purchased={purchasedData} />
        </div>
      </div>
    );
  }

  // Locked, unanswered
  if (disabled) {
    return (
      <div className="space-y-1.5">
        {header}
        <p className="text-[11px] text-tertiary leading-snug">{question}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {header}
      <p className="text-[11px] text-cream/80 leading-snug">{question}</p>
      <YesNoPicker
        yes={main.yes}
        no={main.no}
        onPurchase={onPurchase}
        disabled={disabled}
        purchasingPropId={purchasingPropId}
      />
    </div>
  );
}


/**
 * Yes/No pick for one over/under line.
 *
 * "Yes" and "No" are not a new kind of bet — they are the Over and the Under
 * props that already exist at the same line, so settlement is untouched and
 * each side keeps its own price. Two taps commit (first stages, second buys),
 * the same guard the manual prediction rows use, so no market is one misplaced
 * tap away from spending coins.
 */
function YesNoPicker({ yes, no, onPurchase, disabled, purchasingPropId, compact = false }: {
  yes: PropData | null;
  no: PropData | null;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
  /** Tighter type/padding for the player rows nested inside a game card. */
  compact?: boolean;
}) {
  const [staged, setStaged] = useState<'YES' | 'NO' | null>(null);
  const stageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearStaged = useCallback(() => {
    if (stageTimer.current) clearTimeout(stageTimer.current);
    stageTimer.current = null;
    setStaged(null);
  }, []);
  useEffect(() => () => clearStaged(), [clearStaged]);

  const purchasePending = purchasingPropId !== null;
  // A stale "tap again" must not survive the section locking or a purchase
  // landing from elsewhere. Derived rather than cleared in an effect: the
  // buttons are already disabled by then, so only the prompt needs to go.
  const pending = disabled || purchasePending ? null : staged;

  const handleClick = (side: 'YES' | 'NO', propId: string) => {
    // `pending`, not `staged`: a prompt hidden by a lock must not commit on
    // the first tap after the section re-enables.
    if (pending === side) {
      clearStaged();
      onPurchase(propId);
      return;
    }
    if (stageTimer.current) clearTimeout(stageTimer.current);
    setStaged(side);
    stageTimer.current = setTimeout(() => setStaged(null), 3000);
  };

  const renderSide = (side: 'YES' | 'NO', prop: PropData | null) => {
    if (!prop) return null;
    const isStaged = pending === side;
    const isBuying = purchasingPropId === prop.id;
    return (
      <button
        type="button"
        disabled={disabled || purchasePending}
        onClick={() => handleClick(side, prop.id)}
        aria-label={isStaged ? `Tap again to confirm ${side}` : `Answer ${side} for ${prop.costCoins} coins`}
        className={cn(
          'flex-1 rounded-md border font-bold leading-tight transition-colors',
          'focus:outline-none focus-visible:ring-1 focus-visible:ring-gold/50',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          compact ? 'py-0.5 px-1.5' : 'py-1.5 px-3',
          isStaged
            ? 'bg-gold/40 text-gold border-gold ring-1 ring-gold animate-pulse'
            : side === 'YES'
              ? 'bg-verdigris/10 text-verdigris-text hover:bg-verdigris/20 border-verdigris/30'
              : 'bg-oxblood/10 text-oxblood-text hover:bg-oxblood/20 border-oxblood/30',
        )}
      >
        <span className={cn('leading-tight', compact ? 'text-[10px]' : 'text-[11px]')}>
          {isBuying ? 'Buying...' : isStaged ? `✓ ${side}` : side}
        </span>
        {!isBuying && (
          <span className={cn('block font-normal opacity-70 leading-tight', compact ? 'text-[8px]' : 'text-[9px]')}>
            {prop.bonusMultiplier}x &middot; {prop.costCoins}c
          </span>
        )}
      </button>
    );
  };

  return (
    <div className="flex items-stretch gap-1">
      {renderSide('YES', yes)}
      {renderSide('NO', no)}
    </div>
  );
}


/** The answer a purchased over/under prop stands for. */
function answerOf(prop: PropData, sideOf: (p: PropData) => 'OVER' | 'UNDER' | null): 'Yes' | 'No' {
  return sideOf(prop) === 'UNDER' ? 'No' : 'Yes';
}


/** Purchased/resolved chip shared by the total and player-prop rows. */
function PropResultChip({ purchased }: { purchased: MyPropData | null }) {
  const isResolved = purchased?.result === 'HIT' || purchased?.result === 'MISS';
  return (
    <span className={cn(
      'ml-auto px-2 py-0.5 rounded text-[10px] font-bold',
      isResolved
        ? purchased?.result === 'HIT' ? 'bg-verdigris/20 text-verdigris-text' : 'bg-oxblood/20 text-oxblood-text'
        : 'bg-gold/10 text-gold',
    )}>
      {isResolved
        ? (purchased?.result === 'HIT' ? `HIT +${fmtPts(purchased?.bonusPoints ?? 0)}` : 'MISS')
        : purchased?.result === 'PUSH' ? 'PUSH' : 'Locked in'}
    </span>
  );
}


/**
 * Game total — one plain question, answered Yes or No.
 *
 * The market only ever asks one thing ("do these two teams clear N between
 * them?"), so the card asks exactly that. The alt-line slider that used to sit
 * here is gone: it made the user shop a decimal before they could answer, and
 * the line it started on was the same primary line this question is built from.
 * Yes buys the Over at that line, No buys the Under.
 */
export function TotalSection({ game, props: totalProps, myProps, purchasedPropIds, onPurchase, disabled, purchasingPropId, sport }: {
  game: GameData;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
  /** Event sport — drives the section label (NFL says "Total Points"). */
  sport?: string;
}) {
  const totalLabel = getTotalLabel(sport, game.homeTeam);
  const noun = getTotalNoun(sport, game.homeTeam);
  const main = findMainLine(totalProps, getTotalPropSide);

  const purchasedProp = totalProps.find((p) => purchasedPropIds.has(p.id)) ?? null;
  const purchasedData = purchasedProp
    ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null
    : null;

  if (!main) return null;

  const question = buildTotalQuestion(main.threshold, noun);
  const tooltip = canPush(main.line)
    ? `Yes if the two teams score ${main.threshold} or more ${noun} between them. Exactly ${main.line} refunds either answer.`
    : `Yes if the two teams score ${main.threshold} or more ${noun} between them, No if they fall short.`;

  const header = (
    <div className="flex items-center gap-1.5">
      <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">{totalLabel}</span>
      {!disabled && <InfoTooltip text={tooltip} />}
    </div>
  );

  // Answered
  if (purchasedProp) {
    return (
      <div className="space-y-2">
        {header}
        <p className="text-[11px] text-cream/70 leading-snug">{question}</p>
        <div className="flex items-center gap-2 text-xs">
          <span className="font-bold text-cream">{answerOf(purchasedProp, getTotalPropSide)}</span>
          <span className="text-warm-gray/50">{purchasedProp.bonusMultiplier}x</span>
          <PropResultChip purchased={purchasedData} />
        </div>
      </div>
    );
  }

  // Locked, unanswered
  if (disabled) {
    return (
      <div className="space-y-1.5">
        {header}
        <p className="text-[11px] text-tertiary leading-snug">{question}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {header}
      <p className="text-[11px] text-cream/80 leading-snug">{question}</p>
      <YesNoPicker
        yes={main.yes}
        no={main.no}
        onPurchase={onPurchase}
        disabled={disabled}
        purchasingPropId={purchasingPropId}
      />
    </div>
  );
}


/**
 * Single player prop category — the same plain question, one row tall.
 *
 * Half-point lines exist so nothing can tie, so "Over 24.5" is exactly
 * "25 or more" (see clearingNumber) and the row can ask "25+ Receiving Yards?"
 * instead of making the user read a decimal off a slider.
 */
/**
 * Width of the answer column on a player prop row.
 *
 * The rows are read as a LIST, so the answers have to form a column: a row
 * whose picker sizes to its own content puts every Yes/No at a different x and
 * the list reads as noise. Fixed here, shared by the open and answered states
 * so a row does not jump when it is answered.
 */
const ANSWER_COL = 'w-[112px]';

/**
 * Presentation order for player prop types, mirroring how each sport lists its
 * own markets (config/sports/*.ts propCategories): passing, then rushing, then
 * receiving, then the one-offs. A type missing here sorts to the end
 * alphabetically rather than disappearing — utils.test.ts pins that every
 * labelled player category appears, so a new market cannot silently land last.
 */
const PLAYER_TYPE_ORDER = [
  // NFL
  'PLAYER_PASS_YARDS', 'PLAYER_PASS_TDS', 'PLAYER_PASS_COMPLETIONS',
  'PLAYER_PASS_INTERCEPTIONS', 'PLAYER_RUSH_YARDS', 'PLAYER_RUSH_ATTEMPTS',
  'PLAYER_RECEIVING_YARDS', 'PLAYER_RECEPTIONS', 'PLAYER_RUSH_RECEIVING_YARDS',
  'PLAYER_ANYTIME_TD', 'PLAYER_KICKING_POINTS', 'PLAYER_SACKS',
  // NBA
  'PLAYER_POINTS', 'PLAYER_REBOUNDS', 'PLAYER_ASSISTS',
  'PLAYER_POINTS_REBOUNDS', 'PLAYER_POINTS_ASSISTS', 'PLAYER_REBOUNDS_ASSISTS',
];

/**
 * One player's line within a prop type — the row a viewer actually answers.
 *
 * The type is named once by the group header above, so the row carries the
 * player and the number and nothing else: "A.J. Brown  25+  [Yes][No]".
 * Repeating "Receiving Yards" on every row is what made the old list unreadable.
 */
function PlayerPropRow({ playerName, props, myProps, purchasedPropIds, onPurchase, disabled, purchasingPropId }: {
  playerName: string;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const main = findMainLine(props, getPlayerPropSide);

  const purchasedProp = props.find((p) => purchasedPropIds.has(p.id)) ?? null;
  const purchasedData = purchasedProp
    ? myProps.find((m) => m.undercardPropId === purchasedProp.id) ?? null
    : null;

  if (!main && !purchasedProp) return null;

  // A lineless market ("Anytime TD") has no number to show — the type header
  // already says everything the row would repeat.
  const lineless = main ? isLinelessProp(main.yes ?? main.no ?? props[0]) : true;
  const threshold = main && !lineless ? `${main.threshold}+` : null;

  const name = (
    <span className="min-w-0 flex-1 truncate text-[13px] text-cream/85">{playerName}</span>
  );
  const number = (
    <span className="w-12 shrink-0 text-right font-mono text-[12px] tabular-nums text-warm-gray/70">
      {threshold ?? ''}
    </span>
  );

  if (purchasedProp) {
    return (
      <div className="flex items-center gap-2 py-1">
        {name}
        {number}
        <div className={cn(ANSWER_COL, 'shrink-0 flex items-center justify-end gap-1.5 text-xs')}>
          <span className="font-bold text-cream">{answerOf(purchasedProp, getPlayerPropSide)}</span>
          <span className="text-warm-gray/50 text-[10px]">{purchasedProp.bonusMultiplier}x</span>
          <PropResultChip purchased={purchasedData} />
        </div>
      </div>
    );
  }

  if (disabled) {
    return (
      <div className="flex items-center gap-2 py-1 text-warm-gray/40">
        <span className="min-w-0 flex-1 truncate text-[13px]">{playerName}</span>
        {number}
        <span className={cn(ANSWER_COL, 'shrink-0')} />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2 py-1">
      {name}
      {number}
      <div className={cn(ANSWER_COL, 'shrink-0')}>
        <YesNoPicker
          compact
          yes={main!.yes}
          no={main!.no}
          onPurchase={onPurchase}
          disabled={disabled}
          purchasingPropId={purchasingPropId}
        />
      </div>
    </div>
  );
}


/**
 * One prop TYPE and every player offered on it.
 *
 * Props are organised type-first, then by player: a viewer shopping the card
 * is asking "who do I like for receiving yards?", not "what can I buy on
 * A.J. Brown?". The old shape was the inverse — an accordion per player, each
 * hiding one row per type — which meant opening six drawers to compare six
 * receivers.
 */
export function PlayerCategoryRow({ category, props, myProps, purchasedPropIds, onPurchase, disabled, purchasingPropId }: {
  category: string;
  props: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string) => void;
  disabled: boolean;
  purchasingPropId: string | null;
}) {
  const baseCategory = getBaseCategory(category);
  const label = CATEGORY_LABELS[baseCategory] || CATEGORY_LABELS[category] || category;

  const byPlayer = new Map<string, PropData[]>();
  for (const prop of props) {
    const name = extractPlayerName(prop.description);
    byPlayer.set(name, [...(byPlayer.get(name) ?? []), prop]);
  }
  // Shortest line first: the easiest number to clear reads as the safest bet,
  // and it gives the column a consistent direction to scan.
  const players = [...byPlayer.entries()].sort((a, b) => {
    const al = findMainLine(a[1], getPlayerPropSide)?.threshold ?? 0;
    const bl = findMainLine(b[1], getPlayerPropSide)?.threshold ?? 0;
    return al - bl || a[0].localeCompare(b[0]);
  });

  if (players.length === 0) return null;

  return (
    // The hairline is the same one Spread and Total already sit under in this
    // card. Type headers used their exact label style with none of their
    // separation, which is why a new market read as one more row rather than a
    // boundary. More space above the header than below binds it to its own
    // rows instead of floating between two groups.
    <div className="border-t border-surface-3 pt-2 pb-1">
      <div className="pb-1.5 text-[12px] font-medium uppercase tracking-wider text-warm-gray/80">
        {label}
      </div>
      {players.map(([name, playerProps]) => (
        <PlayerPropRow
          key={name}
          playerName={name}
          props={playerProps}
          myProps={myProps}
          purchasedPropIds={purchasedPropIds}
          onPurchase={onPurchase}
          disabled={disabled}
          purchasingPropId={purchasingPropId}
        />
      ))}
    </div>
  );
}

export function UnifiedGameCard({ game, pick, onPick, picksDisabled, pointsPerCorrectPick, props: gameProps, playerProps, manualProps: manualGameProps, myProps, purchasedPropIds, onPurchase, propDisabled, purchasingPropId, usedTeams, showPoints = true, sport, settleIndex }: {
  game: GameData;
  pick: PickSelection | null;
  onPick: (gameId: string, winner: PickSelection) => void;
  picksDisabled: boolean;
  pointsPerCorrectPick: number;
  props: PropData[];
  playerProps: PropData[];
  manualProps: PropData[];
  myProps: MyPropData[];
  purchasedPropIds: Set<string>;
  onPurchase: (propId: string, userSelection?: 'YES' | 'NO') => void;
  propDisabled: boolean;
  purchasingPropId: string | null;
  /** SURVIVOR: team display name → week it was burned (see GameWinnerRow). */
  usedTeams?: Map<string, number>;
  /** Hide per-pick points copy (survivor — points are meaningless there). */
  showPoints?: boolean;
  /** Event sport — makes prop labels sport-aware (no soccer terms on NFL). */
  sport?: string;
  /**
   * Index of this card in the week's list. Only used to stagger the settle
   * animation when a slate resolves while the page is open — pass the map
   * index on results day, omit it and every card settles immediately.
   */
  settleIndex?: number;
}) {
  // open by default: the player markets are most of what a card sells, and a
  // 10px label with a chevron was not enough for people to find them
  const [playerPropsExpanded, setPlayerPropsExpanded] = useState(true);

  // Filter out MONEYLINE (replaced by Game Winner) and separate SPREAD vs TOTAL
  const spreadProps = gameProps.filter((p) => p.category === 'SPREAD');
  const totalProps = gameProps.filter((p) => p.category === 'TOTAL_OVER' || p.category === 'TOTAL_UNDER' || p.category === 'TOTAL');

  // Group player props by TYPE, in the order the sport lists its markets.
  const byType = new Map<string, PropData[]>();
  for (const prop of playerProps) {
    const base = getBaseCategory(prop.category || 'OTHER');
    byType.set(base, [...(byType.get(base) ?? []), prop]);
  }
  const sortedTypes = [...byType.entries()].sort((a, b) => {
    const ai = PLAYER_TYPE_ORDER.indexOf(a[0]);
    const bi = PLAYER_TYPE_ORDER.indexOf(b[0]);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi) || a[0].localeCompare(b[0]);
  });
  const playerPropPurchasedCount = playerProps.filter((p) => purchasedPropIds.has(p.id)).length;

  const gameDate = new Date(game.gameTime);
  const timeStr = gameDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    + ' • ' + gameDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  // NFL: the header identity is the helmet alone — no abbreviation text, and
  // rendered ~15% larger than the standard badge. Other sports keep flag + name.
  const awayNfl = getNflTeam(game.awayTeam) !== null;
  const homeNfl = getNflTeam(game.homeTeam) !== null;
  // two helmets pointed at each other fit any card; only a flag-plus-abbreviation
  // header has text worth wrapping into a column on a narrow card
  const helmetHeader = awayNfl && homeNfl;

  return (
    <div className="@container rounded-xl border border-surface-3 bg-surface-2 overflow-hidden w-full">
      {/* Game header bar */}
      <div className="px-4 py-2.5 bg-surface-3/50 border-b border-surface-3">
        <div className="flex items-center justify-between gap-2 min-w-0">
          <div className={cn('flex items-center gap-2 text-sm font-bold text-cream min-w-0', !helmetHeader && '@max-xs:flex-col @max-xs:items-start @max-xs:gap-0.5')}>
            <CountryPopover name={game.awayTeam}>
              <CountryFlag name={game.awayTeam} size={awayNfl ? 32 : 'md'} variant="emoji" facing="right" />
              {game.awaySeed && <span className="text-warm-gray/50 text-xs mr-0.5">({game.awaySeed})</span>}
              {!awayNfl && getAbbrev(game.awayTeam)}
            </CountryPopover>
            <span className={cn('text-warm-gray/40 text-xs font-normal', !helmetHeader && '@max-xs:hidden')}>@</span>
            <CountryPopover name={game.homeTeam}>
              <CountryFlag name={game.homeTeam} size={homeNfl ? 32 : 'md'} variant="emoji" facing="left" />
              {game.homeSeed && <span className="text-warm-gray/50 text-xs mr-0.5">({game.homeSeed})</span>}
              {!homeNfl && getAbbrev(game.homeTeam)}
            </CountryPopover>
          </div>
          {game.winner !== null ? (
            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-verdigris-text bg-verdigris/10 border border-verdigris/30 shrink-0">
              Completed
            </span>
          ) : picksDisabled ? (
            <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider text-gold bg-gold/10 border border-gold/30 shrink-0">
              <Lock className="w-2.5 h-2.5" />
              Locked
            </span>
          ) : (
            <span className="text-[10px] text-warm-gray/50 shrink-0 @max-xs:hidden">{timeStr}</span>
          )}
        </div>
      </div>

      <div className="p-4 space-y-4">
      <GameWinnerRow
        game={game}
        pick={pick}
        onPick={onPick}
        disabled={picksDisabled}
        pointsPerCorrectPick={pointsPerCorrectPick}
        usedTeams={usedTeams}
        showPoints={showPoints}
        settleIndex={settleIndex}
      />
      {/* Compact boosts label — replaces the old page-level section header +
          explainer sentence; guidance lives in the tooltip. */}
      {(spreadProps.length > 0 || totalProps.length > 0 || manualGameProps.length > 0 || playerProps.length > 0) && (
        <div className="flex items-center gap-1.5 pt-1">
          <span className="text-gold/80 text-[10px] font-bold tracking-[0.18em] uppercase">Prediction Boosts</span>
          <InfoTooltip text="Optional add-ons: buy spread, total, and player props with coins. Correct props boost your score — your game winner pick is always free." />
        </div>
      )}
      {spreadProps.length > 0 && (
        <>
          <div className="border-t border-surface-3" />
          <SpreadSection
            game={game}
            props={spreadProps}
            myProps={myProps}
            purchasedPropIds={purchasedPropIds}
            onPurchase={onPurchase}
            disabled={propDisabled}
            purchasingPropId={purchasingPropId}
          />
        </>
      )}
      {totalProps.length > 0 && (
        <>
          <div className="border-t border-surface-3" />
          <TotalSection
            game={game}
            props={totalProps}
            myProps={myProps}
            purchasedPropIds={purchasedPropIds}
            onPurchase={onPurchase}
            disabled={propDisabled}
            purchasingPropId={purchasingPropId}
            sport={sport}
          />
        </>
      )}
      {manualGameProps.length > 0 && (
        <>
          <div className="border-t border-surface-3" />
          <div className="-mx-2 space-y-0.5">
            <div className="px-2 mb-1 text-[10px] text-warm-gray/60 font-medium uppercase tracking-wider">
              Predictions
            </div>
            {manualGameProps.map((prop) => (
              <InlineManualPropRow
                key={prop.id}
                prop={prop}
                gameSettled={game.winner != null}
                purchased={myProps.find((m) => m.undercardPropId === prop.id) ?? null}
                onPurchase={onPurchase}
                disabled={propDisabled}
                purchasingPropId={purchasingPropId}
              />
            ))}
          </div>
        </>
      )}
      </div>

      {/* Player Props — collapsible section at bottom */}
      {sortedTypes.length > 0 && (
        <div className="border-t border-surface-3">
          <button
            onClick={() => setPlayerPropsExpanded(!playerPropsExpanded)}
            className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-surface-3/30 transition-colors"
          >
            <div className="flex items-center gap-2">
              <ChevronDown className={cn('w-3.5 h-3.5 text-warm-gray/40 transition-transform', !playerPropsExpanded && '-rotate-90')} />
              <span className="text-[10px] text-warm-gray/70 font-medium uppercase tracking-wider">Player Props</span>
              <span className="text-[10px] text-warm-gray/40">{sortedTypes.length} markets</span>
            </div>
            <div className="flex items-center gap-2">
              {playerPropPurchasedCount > 0 && (
                <span className="text-[10px] text-gold font-medium">{playerPropPurchasedCount} bought</span>
              )}
            </div>
          </button>
          {playerPropsExpanded && (
            <div>
              {sortedTypes.map(([category, typeProps]) => (
                <div key={category} className="px-4">
                  <PlayerCategoryRow
                    category={category}
                    props={typeProps}
                    myProps={myProps}
                    purchasedPropIds={purchasedPropIds}
                    onPurchase={onPurchase}
                    disabled={propDisabled}
                    purchasingPropId={purchasingPropId}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
